import { motion } from "framer-motion";

/**
 * Connected ecosystem visualization — like the SAP nodes reference but for SAALUVARS modules.
 * Center hub ("SAALUVARS") with orbiting module nodes.
 */
const NODES = [
  { code: "HMS", label: "Hospital", angle: 0 },
  { code: "Rx",  label: "Pharmacy", angle: 45 },
  { code: "Lab", label: "Laboratory", angle: 90 },
  { code: "Dr",  label: "Doctor App", angle: 135 },
  { code: "Nr",  label: "Nurse App", angle: 180 },
  { code: "Pt",  label: "Patient App", angle: 225 },
  { code: "AI",  label: "AI Suite", angle: 270 },
  { code: "Ins", label: "Insurance", angle: 315 },
];

export default function EcosystemGraph() {
  const cx = 300, cy = 300, R = 220;
  return (
    <div className="relative w-full aspect-square max-w-[640px] mx-auto" data-testid="ecosystem-graph">
      <svg viewBox="0 0 600 600" className="w-full h-full">
        <defs>
          <radialGradient id="hub" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#E94B2C" stopOpacity="0.9" />
            <stop offset="60%" stopColor="#E94B2C" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#E94B2C" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="line" x1="0" x2="1">
            <stop offset="0" stopColor="#22D3EE" stopOpacity="0.05" />
            <stop offset="0.5" stopColor="#22D3EE" stopOpacity="0.6" />
            <stop offset="1" stopColor="#22D3EE" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* concentric rings */}
        {[120, 170, 220, 260].map((r, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke="rgba(34,211,238,0.12)" strokeDasharray="4 6" />
        ))}

        {/* glow hub */}
        <circle cx={cx} cy={cy} r="140" fill="url(#hub)" />

        {/* lines */}
        {NODES.map((n, i) => {
          if (n.code === 'AI' || n.code === 'Lab') return null;
          const rad = (n.angle * Math.PI) / 180;
          const x = cx + R * Math.cos(rad);
          const y = cy + R * Math.sin(rad);
          return (
            <motion.line
              key={i}
              x1={cx} y1={cy} x2={x} y2={y}
              stroke="rgba(34,211,238,0.4)"
              strokeWidth="1.4"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.15 + i * 0.06 }}
              viewport={{ once: true }}
            />
          );
        })}

        {/* hub label */}
        <g transform={`translate(${cx} ${cy})`}>
          <circle r="56" fill="#0F172A" stroke="#E94B2C" strokeWidth="2" />
          <text textAnchor="middle" y="-4" fill="#fff" fontFamily="Outfit" fontWeight="700" fontSize="16">SAALUVARS</text>
          <text textAnchor="middle" y="14" fill="#94A3B8" fontFamily="JetBrains Mono" fontSize="9">CORE PLATFORM</text>
        </g>

        {/* nodes */}
        {NODES.map((n, i) => {
          const rad = (n.angle * Math.PI) / 180;
          const x = cx + R * Math.cos(rad);
          const y = cy + R * Math.sin(rad);
          return (
            <motion.g
              key={n.code}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.35 + i * 0.08, type: "spring", stiffness: 110 }}
              viewport={{ once: true }}
              style={{ transformOrigin: `${x}px ${y}px` }}
            >
              <circle cx={x} cy={y} r="42" fill="#0F172A" stroke="#22D3EE" strokeWidth="1.2" />
              <circle cx={x} cy={y} r="46" fill="none" stroke="#22D3EE" strokeOpacity="0.25" strokeDasharray="2 4" />
              <text textAnchor="middle" x={x} y={y - 2} fill="#67E8F9" fontFamily="Outfit" fontWeight="700" fontSize="14">{n.code}</text>
              <text textAnchor="middle" x={x} y={y + 14} fill="#94A3B8" fontFamily="JetBrains Mono" fontSize="8">{n.label.toUpperCase()}</text>
            </motion.g>
          );
        })}

        {/* floating particles */}
        {[...Array(14)].map((_, i) => (
          <motion.circle
            key={`p${i}`}
            cx={cx} cy={cy}
            r="1.8"
            fill="#67E8F9"
            initial={{ opacity: 0 }}
            animate={{
              x: [0, Math.cos(i) * 200, 0],
              y: [0, Math.sin(i * 1.7) * 200, 0],
              opacity: [0, 0.8, 0],
            }}
            transition={{ duration: 6 + (i % 4), repeat: Infinity, delay: i * 0.3 }}
          />
        ))}
      </svg>
    </div>
  );
}
